import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Chip, Stack } from "@mui/material";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { AppBadge, AppSelect } from "../../../shared/components/ui";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import {
  IconCheck,
  IconError,
  IconInfo,
  IconWarning,
} from "../../../shared/components/icons";
import { listPaymentExceptions } from "../api";
import { formatAge, formatDateTime, formatTzs } from "../format";
import {
  EXCEPTION_REASON_LABELS,
  type ExceptionReason,
  type ExceptionStatus,
  type PaymentException,
} from "../types";

const REASON_FILTERS: { value: ExceptionReason | ""; label: string }[] = [
  { value: "", label: "All" },
  { value: "invalid_ref", label: "Invalid ref" },
  { value: "duplicate", label: "Duplicate" },
  { value: "signature_fail", label: "Signature fail" },
  { value: "unmatched_customer", label: "Unmatched" },
];

export function reasonBadge(reason: ExceptionReason) {
  if (reason === "signature_fail")
    return (
      <AppBadge tone="error">
        <IconError size={12} /> {EXCEPTION_REASON_LABELS[reason]}
      </AppBadge>
    );
  if (reason === "duplicate")
    return (
      <AppBadge tone="info">
        <IconInfo size={12} /> {EXCEPTION_REASON_LABELS[reason]}
      </AppBadge>
    );
  return (
    <AppBadge tone="warning">
      <IconWarning size={12} /> {EXCEPTION_REASON_LABELS[reason]}
    </AppBadge>
  );
}

function statusBadge(status: ExceptionStatus) {
  return status === "open" ? (
    <AppBadge tone="warning">
      <IconWarning size={12} /> Open
    </AppBadge>
  ) : (
    <AppBadge tone="success">
      <IconCheck size={12} /> Resolved
    </AppBadge>
  );
}

export function S6ReconciliationQueue() {
  const navigate = useNavigate();
  const [reason, setReason] = useState<ExceptionReason | "">("");
  const [status, setStatus] = useState<ExceptionStatus | "">("open");

  const query = useQuery({
    queryKey: ["payment-exceptions", status, reason],
    queryFn: () =>
      listPaymentExceptions({
        status: status || undefined,
        reason: reason || undefined,
      }),
    retry: 1,
    staleTime: 15_000,
  });

  const columns = useMemo<ColumnDef<PaymentException, unknown>[]>(
    () => [
      { accessorKey: "reference", header: "Reference" },
      {
        accessorKey: "reason",
        header: "Reason",
        enableSorting: false,
        cell: (c) => reasonBadge(c.getValue() as ExceptionReason),
      },
      {
        accessorKey: "amount_cents",
        header: "Amount",
        cell: (c) => formatTzs(c.getValue() as number),
        meta: { hideBelow: "md" },
      },
      {
        accessorKey: "received_at",
        header: "Received",
        cell: (c) => formatDateTime(c.getValue() as string),
        meta: { hideBelow: "lg" },
      },
      {
        id: "age",
        header: "Age",
        accessorFn: (row) => row.received_at,
        cell: (c) => formatAge(c.getValue() as string),
        meta: { hideBelow: "md" },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (c) => statusBadge(c.getValue() as ExceptionStatus),
        meta: { hideBelow: "lg" },
      },
      { accessorKey: "exception_number", header: "Exception", meta: { technical: true } },
      { accessorKey: "id", header: "Exception ID", meta: { technical: true } },
      { accessorKey: "payment_id", header: "Payment ID", meta: { technical: true } },
    ],
    [],
  );

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Reconciliation Queue" subtitle="Payment exceptions requiring review." />
        <PageState status="loading" />
      </>
    );
  }

  if (query.isError) {
    return (
      <>
        <PageHeader title="Reconciliation Queue" subtitle="Payment exceptions requiring review." />
        <PageState
          status="error"
          error={query.error instanceof Error ? query.error.message : "Something went wrong"}
          onRetry={() => void query.refetch()}
        />
      </>
    );
  }

  const rows = query.data.exceptions;
  const title =
    status === "open" ? `Reconciliation Queue (${query.data.total} open)` : "Reconciliation Queue";

  return (
    <>
      <PageHeader
        title={title}
        subtitle="Payment exceptions requiring review."
        actions={
          <AppSelect
            id="exc-status-filter"
            aria-label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ExceptionStatus | "")}
          >
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="">All statuses</option>
          </AppSelect>
        }
      />

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: "wrap", mb: 2 }}
        role="group"
        aria-label="Reason filter"
      >
        {REASON_FILTERS.map((f) => (
          <Chip
            key={f.value || "all"}
            label={f.value === "" ? `${f.label} (${query.data.total})` : f.label}
            color={reason === f.value ? "primary" : "default"}
            variant={reason === f.value ? "filled" : "outlined"}
            onClick={() => setReason(f.value)}
            sx={{ minHeight: { xs: 40, sm: 32 } }}
          />
        ))}
      </Stack>

      {rows.length === 0 ? (
        <PageState
          status="empty"
          emptyTitle="Queue clear"
          emptyHint={`No exceptions${reason ? ` for ${REASON_FILTERS.find((f) => f.value === reason)?.label.toLowerCase()}` : ""}${status ? ` with status "${status}"` : ""}.`}
        />
      ) : (
        <AppDataTable
          data={rows}
          columns={columns}
          onRowClick={(row) => navigate(`/reconciliation/${row.id}`)}
        />
      )}
    </>
  );
}
